import { useCallback, useEffect, useMemo, useState } from 'react';

import { CustomImage, Text, useText } from '@src/components';

import OrderSummaryInformation from './OrderSummaryInformation';
import CardDetailInformation from './CardDetailInformation';
import SimilarOrder from './SimilarOrder';

import { Button, Popconfirm } from 'antd';

import { useDispatch, useSelector } from 'react-redux';
import {
  loadBalancesDelete,
  loadBalancesUpdate,
  setIsAllSelectedRowKeys,
  setSelectedRowKeys,
  setOrderStep,
  getOrderDoubleCheck,
  setRunTimeTotalPrice,
  resetCurrentLoadBalance,
} from '@src/store/slice/balanceUploadSlice';
import {
  getLoadBalanceDetailsGetPagedList,
  resetBalanceFilterItems,
  setBalanceUploadClear,
  setBalanceUploadDetails,
} from '@src/store/slice/balanceUploadDetailSlice';

import iconBack from '@src/assets/icons/icon-back.svg';
import '@src/styles/balanceUpload/orderSummary.scss';

import { useMediaQuery } from '@src/hooks/useMediaQuery';
import { handleAuthStatus, isCreditCardUser } from '@src/utils/utils';
import { unwrapResult } from '@reduxjs/toolkit';
import OrderSummaryInformationCompliments from './OrderSummaryInformationCompliments';
import PropTypes from 'prop-types';
import { VouIdCode } from '@src/store/enums';
import { useHistory } from 'react-router-dom';
import { confirmDialog } from '@src/components/CustomDialog';
import { getCustomerVouListForGift } from '@src/store/slice/customerSlice';

const OrderSummary = ({
  isCompliments: isComplimentsProp = false,
  isMarket: isMarketProp = false,
  vouId: propVouId,
}) => {
  const isCompliments = isComplimentsProp || propVouId === VouIdCode.Compliments;
  const isMarket = isMarketProp || propVouId === VouIdCode.Market;
  const isMobil = useMediaQuery({ query: '(max-width: 1200px)' });
  const dispatch = useDispatch();
  const { currentLoadBalance } = useSelector((state) => state?.balanceUpload);
  const { customerVou, selectedCustomer } = useSelector((state) => state?.customer);
  const { userAuthorizationStatus, userAuthorizationGiftStatus, userAuthorizationMarketStatus } =
    useSelector((state) => state?.user);
  const [similarOrderModalVisible, setSimilarOrderModalVisible] = useState(false);

  const history = useHistory();
  const [isExit, setIsExit] = useState(false);

  const vouId = useMemo(() => {
    if (isMarket) return VouIdCode.Market;
    if (isCompliments) return VouIdCode.Compliments;
    return VouIdCode.Restaurant;
  }, [isMarket, isCompliments]);

  const userAuthorizationStatusForType = useMemo(() => {
    if (isMarket) return userAuthorizationMarketStatus;
    if (isCompliments) return userAuthorizationGiftStatus;
    return userAuthorizationStatus;
  }, [isMarket, isCompliments]);

  const warningHtmlContent = <Text t="whenYouLeaveThisPageTheInformationYou" />;

  const giveUpText = useText('giveUp');

  useEffect(() => {
    const historyBlock = history.block((location) => {
      if (isExit) {
        return true;
      }
      if (
        currentLoadBalance?.id &&
        currentLoadBalance?.vouId === VouIdCode.Compliments &&
        currentLoadBalance.pendingApproval !== true
      ) {
        confirmDialog({
          title: <Text t="attention" />,
          htmlContent: warningHtmlContent,
          okText: <Text t="yes" />,
          cancelText: <Text t={giveUpText} />,
          closeIcon: false,
          onCancel: async (e) => {
            setIsExit(false);
            return false;
          },
          onOk: async () => {
            if (currentLoadBalance?.id > 0) {
              const body = { id: currentLoadBalance?.id };
              await dispatch(loadBalancesDelete(body));
            }
            setIsExit(true);
            await dispatch(setOrderStep(null));
            await dispatch(resetCurrentLoadBalance());
            await dispatch(setRunTimeTotalPrice(0));
            history.push(location.pathname);
          },
        });
      } else {
        return true;
      }

      return false;
    });

    return historyBlock;
  }, [history, isExit, dispatch, currentLoadBalance]);

  useEffect(async () => {
    window.scrollTo(0, 0);
    await dispatch(setBalanceUploadDetails([]));
  }, []);

  async function setOrderStatus(userAuthorizationStatus, customerVou, body) {
    const isAuthorized = (group) => handleAuthStatus(userAuthorizationStatus || [], group);

    let complimentsPamId = null;
    let complimentsCustomerVou = null;
    if (isCompliments || isMarket) {
      const giftCustomerInfo = await dispatch(
        getCustomerVouListForGift({
          customerId: selectedCustomer?.customerId,
          vouId: vouId,
        }),
      );
      if (giftCustomerInfo?.payload?.data[0]?.pamId) {
        complimentsPamId = giftCustomerInfo?.payload?.data[0]?.pamId;
        complimentsCustomerVou = giftCustomerInfo?.payload?.data[0];
      }
    }

    if (!isAuthorized('ApprovalAuthorizationGroup') || !isAuthorized('PaymentAuthorizationGroup')) {
      if (
        isCreditCardUser(
          (isCompliments || isMarket) && complimentsCustomerVou
            ? complimentsCustomerVou
            : customerVou,
        )
      ) {
        body.orderStatus = 20;
      } else {
        body.sendMail = true;
        body.orderStatus = 90;
        body.paymentType =
          (isCompliments || isMarket) && complimentsPamId ? complimentsPamId : customerVou?.pamId;
      }
    } else {
      body.orderStatus = 15;
      body.sendMail = true;
      body.pendingApproval = true;
      if (!isCreditCardUser(customerVou)) {
        body.paymentType =
          (isCompliments || isMarket) && complimentsPamId ? complimentsPamId : customerVou?.pamId;
      }
    }
    if ((isCompliments || isMarket) && complimentsPamId === 5) {
      body.dontSendBinAndToken = true;
    }

    return body;
  }

  const handleOrderApproval = useCallback(
    async (event, checkedDoubleOrder = false) => {
      const body = {
        ...currentLoadBalance,
      };
      const orderDoubleResult = await dispatch(
        getOrderDoubleCheck({
          loadBalanceId: currentLoadBalance?.id,
          customerId: currentLoadBalance?.customerId,
          pageNumber: 1,
        }),
      );

      const orderDoubleData = unwrapResult(orderDoubleResult);

      if (
        orderDoubleData.data?.items &&
        orderDoubleData.data?.items.length > 0 &&
        !checkedDoubleOrder
      ) {
        setSimilarOrderModalVisible(true);
      } else {
        const orderBody = await setOrderStatus(userAuthorizationStatusForType, customerVou, body);

        setSimilarOrderModalVisible(false);
        await dispatch(loadBalancesUpdate(orderBody));
      }
    },
    [currentLoadBalance, dispatch, customerVou],
  );

  const handleOrderBack = useCallback(async () => {
    const body = {
      ...currentLoadBalance,
      orderStatus: 0,
    };

    if (isCompliments) {
      await dispatch(setSelectedRowKeys([]));
      await dispatch(setIsAllSelectedRowKeys(false));
      await dispatch(loadBalancesDelete());
      setTimeout(async () => {
        await dispatch(setBalanceUploadClear());
      }, 1250);
    } else {
      await dispatch(loadBalancesUpdate(body));
      await dispatch(
        getLoadBalanceDetailsGetPagedList({
          body: {
            loadBalanceId: currentLoadBalance?.id,
            vouId: vouId,
          },
          pageNumber: 1,
        }),
      );
    }
  }, [currentLoadBalance, isCompliments, isMarket, dispatch]);

  const handleCancelOrder = useCallback(async () => {
    await dispatch(loadBalancesDelete());
    await dispatch(setSelectedRowKeys([]));
    await dispatch(setIsAllSelectedRowKeys(false));
    await dispatch(setOrderStep(null));
    await dispatch(resetBalanceFilterItems());
  }, [dispatch]);

  return (
    <>
      <SimilarOrder
        similarOrderModalVisible={similarOrderModalVisible}
        setSimilarOrderModalVisible={setSimilarOrderModalVisible}
        handleOrderApproval={handleOrderApproval}
      />
      <div className="balance-upload-summary-content">
        {isCompliments || isMarket ? (
          <OrderSummaryInformationCompliments />
        ) : (
          <OrderSummaryInformation />
        )}
        {(isCompliments || isMarket) && (
          <CardDetailInformation isCompliments={isCompliments} isMarket={isMarket} />
        )}
        <div
          className="step-btn-container-summary-balance"
          style={{
            backgroundColor: isCompliments || isMarket ? '#fff' : 'transparent',
            borderTopLeftRadius: isCompliments || isMarket ? 8 : 0,
            borderTopRightRadius: isCompliments || isMarket ? 8 : 0,
            paddingBottom: isCompliments || isMarket ? 20 : 0,
            paddingRight: isCompliments || isMarket ? 12 : 0,
            paddingTop: isCompliments || isMarket ? 20 : 0,
            marginBottom: isCompliments || isMarket ? 0 : 38,
            marginTop: isCompliments || isMarket ? 20 : 0,
            position: isCompliments || isMarket ? 'sticky' : 'relative',
            bottom: isCompliments || isMarket ? 0 : null,
          }}
        >
          {!(
            currentLoadBalance?.orderStatus === 10 &&
            currentLoadBalance?.orderProcessType === 3 &&
            currentLoadBalance?.pendingApproval
          ) ? (
            <Button
              id="balanceUpload-orderSummary-main-back-btn"
              className="back-btn"
              onClick={handleOrderBack}
            >
              <CustomImage src={iconBack} />
              <Text t="turnBack" />
            </Button>
          ) : null}

          {!(isCompliments || isMarket) && isMobil ? (
            <CardDetailInformation isCompliments={isCompliments} isMarket={isMarket} />
          ) : null}

          <div
            className="right-btn-container"
            style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}
          >
            {currentLoadBalance?.pendingApproval !== true && (
              <Popconfirm
                title={<Text t="areYouSureYouWantToCancelThe" />}
                onConfirm={handleCancelOrder}
                onCancel={() => {}}
                okText={<Text t="cancel" />}
                cancelText={giveUpText}
              >
                <Button id="balanceUpload-orderSummary-main-cancel-btn" className="cancel-btn">
                  <Text t="cancelOrder" />
                </Button>
              </Popconfirm>
            )}

            <Button
              id="balanceUpload-orderSummary-main-next-btn"
              className="next-btn"
              onClick={handleOrderApproval}
            >
              <Text t="approveOrder" />
            </Button>
          </div>
        </div>
        {!(isCompliments || isMarket) && !isMobil ? (
          <CardDetailInformation isCompliments={isCompliments} isMarket={isMarket} />
        ) : null}
      </div>
    </>
  );
};

OrderSummary.propTypes = {
  isCompliments: PropTypes.bool,
  isMarket: PropTypes.bool,
  vouId: PropTypes.number,
};

export default OrderSummary;
